import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
// The public package stylesheet is the single runtime boundary for every
// system-owned component selector. Importing the component API alone is not
// enough in the built package because Vite extracts its CSS to `styles.css`.
import "@juice/design-system/styles.css";
import App from "./App";
import { installChunkReload } from "./lib/chunk-reload";
import { bootAccountV2TestAuth } from "./lib/account-v2-test-auth";
import { syncEditorDevAuth } from "./lib/dev-auth";
import { queryClient } from "@/app/providers";

async function bootstrap() {
  void import('./instrument')
    .then(({ initSentry }) => initSentry())
    .catch((error: unknown) => {
      console.error('[telemetry] Failed to initialize Sentry', error)
    })
  // Recover from stale code-split chunks after a deploy (see chunk-reload.ts).
  // Installed before render so the handler is attached before any lazy import.
  installChunkReload();

  if (import.meta.env.VITE_USE_MOCKS === "1") {
    const { worker, installInputCanvasMockDevtools } = await import(
      "@/mocks/browser"
    );
    // Seed the Account-Management-v2 mock store and (in the browser) publish the
    // `window.__accMgmt` role harness. Handlers read the seeded matrix scenario;
    // role switching is driven by `?devUser=<uid>` (dev-auth bearer) or
    // `window.__accMgmt.setActiveUser(...)`. Inert for the agentic mock — the
    // acc-mgmt handlers only intercept the disjoint `/v2/{me,workspaces,invites,
    // plans}` namespace.
    const { installAccMgmtDevtools } = await import("@/mocks/acc-mgmt-v2");
    installAccMgmtDevtools();
    // 'bypass' so Firebase token refresh (securetoken.googleapis.com) and
    // Sentry traffic pass through to the network unmodified.
    await worker.start({ onUnhandledRequest: "bypass", quiet: true });
    installInputCanvasMockDevtools(queryClient);
  }
  await bootAccountV2TestAuth();
  // Dev only: hand the local dev sign-in to the embedded Video editor (same origin, under /studio/).
  // A no-op in a production build; the function and its key are dropped from the bundle.
  syncEditorDevAuth();

  // Expose the Zustand store registry on window for the regression suite's
  // state-layer snapshots (e2e/regression/_lib/snapshot.ts). Dev builds
  // only: `import.meta.env.PROD` is a build-time constant, so the dynamic
  // import below is dead-code-eliminated from production bundles.
  if (!import.meta.env.PROD) {
    const { STORE_REGISTRY } = await import("@/regression/store-registry");
    (window as unknown as {
      __regressionStores?: Record<string, { getState: () => unknown }>
    }).__regressionStores = STORE_REGISTRY as unknown as Record<
      string,
      { getState: () => unknown }
    >;
  }

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );
}

void bootstrap();
